import { useAuth } from "@/contexts/AuthContext";

type Props = {
  onNavigate: (page: "timeline" | "admin") => void;
};

export function Dashboard({ onNavigate }: Props) {
  const { user, logout } = useAuth();

  if (!user) return null;

  const roleColors = {
    admin: "bg-primary-500/10 text-primary-400 border-primary-500/20",
    editor: "bg-primary-500/10 text-primary-400 border-primary-500/20",
    viewer: "bg-surface text-muted border-border",
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 bg-primary-500 rounded flex items-center justify-center">
                <span className="text-background font-bold text-xs">IF</span>
              </div>
              <span className="text-foreground font-medium text-sm">Idea Factory</span>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                {user.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt={user.name || "User"}
                    className="w-7 h-7 rounded-full"
                  />
                ) : (
                  <div className="w-7 h-7 bg-surface rounded-full flex items-center justify-center">
                    <span className="text-muted text-xs font-medium">
                      {user.name?.[0] || user.email[0].toUpperCase()}
                    </span>
                  </div>
                )}
                <span className="text-muted text-sm hidden sm:block">
                  {user.name || user.email}
                </span>
              </div>

              <button
                onClick={logout}
                className="text-muted-foreground hover:text-foreground text-sm transition-colors"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center">
          <h1 className="text-3xl font-medium text-foreground mb-3">
            Welcome, {user.name?.split(' ')[0] || "there"}
          </h1>

          <div className="flex items-center justify-center gap-2 mb-10">
            <span
              className={`px-2.5 py-0.5 rounded text-xs font-medium border ${roleColors[user.role]}`}
            >
              {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
            </span>
            <span className="text-muted-foreground">•</span>
            <span className="text-muted text-sm">{user.email}</span>
          </div>

          <div className="bg-surface border border-border rounded-lg p-8 max-w-3xl mx-auto">
            <p className="text-muted mb-8 text-sm">
              Bell Labs documentary pre-production workspace
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <FeatureCard
                title="Timeline"
                description="Visual timeline of events and eras"
                icon="📅"
                onClick={() => onNavigate("timeline")}
                active
              />
              {user.role === "admin" && (
                <FeatureCard
                  title="User Management"
                  description="Invite users and manage roles"
                  icon="👤"
                  onClick={() => onNavigate("admin")}
                  active
                />
              )}
              <FeatureCard
                title="Interviews"
                description="Manage subjects and questions"
                icon="🎤"
                disabled
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function FeatureCard({
  title,
  description,
  icon,
  onClick,
  active,
  disabled,
}: {
  title: string;
  description: string;
  icon: string;
  onClick?: () => void;
  active?: boolean;
  disabled?: boolean;
}) {
  const baseClasses = "rounded-lg p-5 text-left transition-all";
  const activeClasses = active
    ? "bg-primary-500/5 border border-primary-500/30 hover:border-primary-500/50 cursor-pointer"
    : disabled
    ? "bg-surface-elevated border border-border opacity-40 cursor-not-allowed"
    : "bg-surface-elevated border border-border hover:border-border-subtle cursor-pointer";

  return (
    <div
      className={`${baseClasses} ${activeClasses}`}
      onClick={disabled ? undefined : onClick}
    >
      <div className="text-xl mb-3">{icon}</div>
      <h3 className="text-foreground font-medium text-sm mb-1">{title}</h3>
      <p className="text-muted-foreground text-xs">{description}</p>
      {active && (
        <div className="mt-4 flex items-center gap-1 text-primary-500 text-xs font-medium">
          Open
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      )}
      {disabled && (
        <div className="mt-4 text-muted-foreground text-xs">Coming soon</div>
      )}
    </div>
  );
}
